import { useState, useEffect, useCallback } from "react";
import {
  workPerformanceApi, STEP_OPTIONS, STEP_MAP,
  loadStepOptions,   // ← 추가
} from "./workPerformanceApi";


import WorkPerformanceDetail from "./WorkPerformanceDetail";
import styles from "./WorkPerformancePage.module.css";
import api from "../../api";
import { sortEndedLast, withEndedLabel } from "../../utils/contractEnded";

const toKST = () => new Date(new Date().getTime() + 9 * 60 * 60 * 1000);
const thisYear = toKST().getFullYear();
const thisMonth = String(toKST().getMonth() + 1).padStart(2, "0");

function getWeekRange() {
  const now = toKST();
  const day = now.getDay();
  const mon = new Date(now); mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return [mon.toISOString().slice(0, 10), sun.toISOString().slice(0, 10)];
}

export default function WorkPerformanceSummaryPage() {
  const [startDate, setStartDate] = useState(`${thisYear}-${thisMonth}-01`);
  const [endDate,   setEndDate]   = useState(toKST().toISOString().slice(0, 10));
  const [empFilter, setEmpFilter] = useState("");
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [empList,   setEmpList]   = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");   

  // 직원 목록
  useEffect(() => {
    api.get("users/users/").then(r => {
      const raw = Array.isArray(r.data) ? r.data : (r.data.results || []);
      // 조회용 목록 — 계약종료자도 과거 성과를 볼 수 있게 남기고 맨 아래로(표기는 렌더링 시점)
      setEmpList(sortEndedLast(raw.filter(u => u.name && !u.name.includes("테스트"))));
    }).catch(() => {});
  }, []);

  useEffect(() => { loadStepOptions(); }, []);

  
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (empFilter) params.employee = empFilter;
      const data = await workPerformanceApi.list(params);
      setLogs(data?.results ?? data ?? []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [startDate, endDate, empFilter]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleToggleStep = async (logId, stepId, isDone) => {
    try {
      const updatedStep = await workPerformanceApi.toggleStep(logId, stepId, isDone);
      const updateSteps = (steps) => steps.map(s => s.id === stepId ? { ...s, ...updatedStep } : s);
      setLogs(prev => prev.map(l => l.id === logId ? { ...l, steps: updateSteps(l.steps) } : l));
      setDetailItem(prev => prev?.id === logId ? { ...prev, steps: updateSteps(prev.steps) } : prev);
    } catch (e) { alert("처리 실패: " + e.message); }
  };

  const handleDelete = async (id) => {
    try {
      await workPerformanceApi.remove(id);
      setLogs(prev => prev.filter(l => l.id !== id));
      setDetailItem(null);
    } catch (e) { alert("삭제 실패: " + e.message); }
  };

  // ── 상태 판정 ────────────────────────────────────────────
  const getStatus = (l) => {
    const doneN = l.steps.filter(s => s.is_done).length;
    if (doneN === 0) return "wait";
    if (doneN === l.steps.length) return "done";
    return "progress";
  };
  const statusCounts = { all: logs.length, wait: 0, progress: 0, done: 0 };
  logs.forEach(l => { statusCounts[getStatus(l)]++; });
  const filteredLogs = statusFilter === "all"
    ? logs
    : logs.filter(l => getStatus(l) === statusFilter);

  // ── 집계 ─────────────────────────────────────────────────
  const totalCount = filteredLogs.length;
  const doneCount  = filteredLogs.filter(l => l.steps.every(s => s.is_done)).length;
  const getMin = (s) => s.minutes ?? STEP_MAP[s.step_type]?.defaultMin ?? 5;
  const totalMin = filteredLogs.reduce((a, l) => a + l.steps.reduce((b, s) => b + getMin(s), 0), 0);
  const doneMin  = filteredLogs.reduce((a, l) => a + l.steps.filter(s => s.is_done).reduce((b, s) => b + getMin(s), 0), 0);
  const doneRate = totalMin ? Math.round((doneMin / totalMin) * 100) : 0;

  // 날짜 범위 일수
  const dayCount = Math.max(1, Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1);
  const avgPerDay = (totalCount / dayCount).toFixed(1);
  const avgMinPerDay = Math.round(totalMin / dayCount);

  // 단계별 집계
  const stepCounts = {};
  filteredLogs.forEach(l => l.steps.forEach(s => {


    stepCounts[s.step_type] = (stepCounts[s.step_type] || 0) + 1;
  }));
  const maxStepCount = Math.max(...Object.values(stepCounts), 1);

  // 일별 그룹
  const byDate = {};
  filteredLogs.forEach(l => {


    if (!byDate[l.work_date]) byDate[l.work_date] = [];
    byDate[l.work_date].push(l);
  });
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  const dayLabel = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    const m = dateStr.slice(5, 7); const day = dateStr.slice(8, 10);
    return `${m}월 ${day}일 (${days[d.getDay()]})`;
  };

  return (
    <div style={{ padding: "16px 20px", fontFamily: "'Noto Sans KR', sans-serif" }}>

      {/* ── 상단 툴바 ──────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, flexWrap: "nowrap" }}>
        
        <span style={{ fontSize: 16, fontWeight: 500, color: "#0f172a", whiteSpace: "nowrap" }}>업무처리 현황</span>

        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          style={{ border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "4px 8px", fontSize: 12, outline: "none", width: 130 }} />


        <span style={{ fontSize: 12, color: "#94a3b8" }}>~</span>
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          style={{ border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "4px 8px", fontSize: 12, outline: "none", width: 130 }} />

        
        <select value={empFilter} onChange={e => setEmpFilter(e.target.value)}
          style={{ border: "0.5px solid #e2e8f0", borderRadius: 6, padding: "4px 8px", fontSize: 12, outline: "none", width: 110 }}>


          <option value="">전체 직원</option>
          {empList.map(u => <option key={u.id} value={u.id}>{withEndedLabel(u.name, u.is_contract_ended)}</option>)}
        </select>
        <button className={styles.btnGhost} onClick={() => {
          setStartDate(`${thisYear}-${thisMonth}-01`);
          setEndDate(toKST().toISOString().slice(0, 10));
        }}>이번 달</button>
        <button className={styles.btnGhost} onClick={() => {
          const [s, e] = getWeekRange();
          setStartDate(s); setEndDate(e);
        }}>이번 주</button>
        <button className={styles.btnGhost} onClick={loadLogs}>↺</button>
      </div>

      {/* ── 집계 카드 + 상태 필터 ───────────────────────────── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "stretch" }}>

        {/* 왼쪽: 집계 카드 4개 (폭 축소) */}
        <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
          {[
            { label: "총 업무 건수", val: `${totalCount}건`, sub: `완료 ${doneCount} · 진행중 ${totalCount - doneCount}` },
            { label: "총 처리 시간", val: `${totalMin}분`, sub: `완료 ${doneMin}분 · 미완 ${totalMin - doneMin}분` },
            { label: "처리율", val: `${doneRate}%`, sub: "단계 기준", color: "#10b981" },
            { label: "일 평균", val: `${avgPerDay}건`, sub: `${avgMinPerDay}분 / 일` },
          ].map(card => (
            <div key={card.label} style={{
              background: "#fff", border: "0.5px solid #e2e8f0",
              borderRadius: 10, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>{card.label}</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: card.color || "#0f172a" }}>{card.val}</div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{card.sub}</div>
            </div>
          ))}
        </div>

        {/* 오른쪽: 상태 필터 카드 4개 */}
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {[
            { key: "all",      label: "전체",   color: "#3b82f6" },
            { key: "wait",     label: "대기",   color: "#94a3b8" },
            { key: "progress", label: "진행중", color: "#f59e0b" },
            { key: "done",     label: "완료",   color: "#10b981" },
          ].map(f => {
            const active = statusFilter === f.key;
            return (
              <div key={f.key} onClick={() => setStatusFilter(f.key)} style={{
                width: 72, cursor: "pointer",
                background: active ? f.color + "12" : "#fff",
                border: `0.5px solid ${active ? f.color : "#e2e8f0"}`,
                borderRadius: 10, padding: "12px 14px",
              }}>
                <div style={{ fontSize: 11, color: active ? f.color : "#94a3b8", marginBottom: 4 }}>{f.label}</div>
                <div style={{ fontSize: 20, fontWeight: 500, color: active ? f.color : "#0f172a" }}>{statusCounts[f.key]}</div>
              </div>
            );
          })}
        </div>



      </div>



      {/* ── 단계별 집계 바 ─────────────────────────────────── */}
      {Object.keys(stepCounts).length > 0 && (
        <div style={{
          background: "#fff", border: "0.5px solid #e2e8f0",
          borderRadius: 10, padding: "12px 16px", marginBottom: 14,
        }}>
          <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 10 }}>단계별 처리 횟수</div>
          {STEP_OPTIONS.filter(o => stepCounts[o.key]).sort((a, b) => (stepCounts[b.key] || 0) - (stepCounts[a.key] || 0)).map(opt => (
            <div key={opt.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: "#64748b", width: 100, flexShrink: 0, whiteSpace: "nowrap" }}>
                {opt.icon} {opt.label}
              </span>
              <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  width: `${Math.round((stepCounts[opt.key] / maxStepCount) * 100)}%`,
                  background: opt.color,
                }} />
              </div>
              <span style={{ fontSize: 11, color: "#94a3b8", width: 20, textAlign: "right" }}>
                {stepCounts[opt.key]}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── 일별 그룹 목록 ─────────────────────────────────── */}
      {loading && <div style={{ textAlign: "center", padding: 30, color: "#94a3b8", fontSize: 13 }}>불러오는 중…</div>}
      {!loading && filteredLogs.length === 0 && (
  
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 13 }}>등록된 업무가 없습니다.</div>
      )}
      {!loading && sortedDates.map(dateStr => {
        const dayLogs = byDate[dateStr];
        const dayTotal = dayLogs.reduce((a, l) => a + l.steps.reduce((b, s) => b + getMin(s), 0), 0);
        return (
          <div key={dateStr} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: "#374151" }}>{dayLabel(dateStr)}</span>
              <div style={{ flex: 1, height: 4, background: "#f1f5f9", borderRadius: 4, maxWidth: 100, overflow: "hidden" }}>
                <div style={{ height: "100%", borderRadius: 4, background: "linear-gradient(90deg,#3b82f6,#10b981)", width: "60%" }} />
              </div>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>{dayLogs.length}건 · {dayTotal}분</span>
            </div>
            {dayLogs.map(item => {
              const allDone  = item.steps.every(s => s.is_done);
              const iTotal   = item.steps.reduce((a, s) => a + getMin(s), 0);
              return (
                <div key={item.id} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: "#fff", border: "0.5px solid #e2e8f0",
                  borderRadius: 8, padding: "8px 14px", marginBottom: 5,
                }}>
                  <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", width: 38, flexShrink: 0 }}>
                    {item.created_at ? new Date(item.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: false }) : "—"}
                  </span>
                  <span style={{ fontSize: 13, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180 }}>
                    {item.title}
                  </span>
                  <span style={{
                    fontSize: 10, padding: "1px 6px", borderRadius: 10, whiteSpace: "nowrap", flexShrink: 0,
                    background: allDone ? "#d1fae5" : "#fef3c7",
                    color: allDone ? "#065f46" : "#92400e",
                  }}>
                    {allDone ? "완료" : "진행중"}
                  </span>
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap", flex: 1 }}>
                    {item.steps.map(step => {
                      const cfg = STEP_MAP[step.step_type];
                      if (!cfg) return null;
                      return (
                        <span key={step.id} style={{
                          fontSize: 10, padding: "2px 6px", borderRadius: 10, whiteSpace: "nowrap",
                          background: step.is_done ? cfg.color + "18" : "#f1f5f9",
                          color: step.is_done ? cfg.color : "#94a3b8",
                          border: `0.5px solid ${step.is_done ? cfg.color + "40" : "#e2e8f0"}`,
                        }}>
                          {cfg.icon} {cfg.label}{step.is_done ? " ✓" : ""}
                        </span>
                      );
                    })}
                  </div>
                  <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>{iTotal}분</span>
                  <span style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", flexShrink: 0 }}>{item.employee_name}</span>
                  <button className={styles.btnGhost}
                    style={{ fontSize: 11, padding: "2px 8px", flexShrink: 0 }}
                    onClick={() => setDetailItem(item)}>
                    상세 →
                  </button>
                </div>
              );
            })}
          </div>
        );
      })}

      {detailItem && (
        <WorkPerformanceDetail
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onToggleStep={handleToggleStep}
          onEdit={() => {}}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}